import * as React from "react"
import { type EntityCategory } from "@digits-graphql/frontend/graphql-bearer"
import { ThSort } from "@digits-shared/DesignSystem/Tables/tableStyles"
import stringHelper from "@digits-shared/helpers/stringHelper"
import styled from "styled-components"
import {
  type CategoryTableFooter,
  type CategoryTableMetadata,
  type CategoryTableVariants,
  type ExportOptions,
  type TableCollapsingState,
} from "src/frontend/components/Shared/Transactions/Categories/categoryTableTypes"
import { useCategoryTransactionsTableColumns } from "src/frontend/components/Shared/Transactions/Categories/useCategoryTransactionsTableColumns"
import { type TransactionsTableProps } from "src/frontend/components/Shared/Transactions/types"
import { VTransactionsTable } from "src/frontend/components/Shared/Transactions/VTransactionsTable"
import type { HierarchicalDimensionSummaryNode } from "src/shared/helpers/hierarchicalDimensionHelper"

const CategoryVTable = styled(VTransactionsTable)`
  col[data-column-name="runningTotal"] {
    transition: width 200ms ease-out;
  }
  &.hide-balances {
    col[data-column-name="runningTotal"] {
      width: 0 !important;
      min-width: 0 !important;
    }

    th[data-header="moneyFlow"],
    td[data-column="moneyFlow"] {
      padding-right: 16px;
    }
  }

  // Amounts
  th[data-header="moneyFlow"],
  td[data-column="moneyFlow"],
  td[data-column="runningTotal"] {
    text-align: right;

    ${ThSort} {
      justify-content: flex-start;
      flex-direction: row-reverse;
    }
  }
`

/*
  INTERFACES
*/

type TableProps = TransactionsTableProps & {
  categorySummary: HierarchicalDimensionSummaryNode<EntityCategory> | undefined
  hideBalanceColumn?: boolean
  collapsing?: TableCollapsingState
  exportOptions?: ExportOptions
  Footer?: CategoryTableFooter
} & CategoryTableVariants

/*
  COMPONENTS
*/

export const CategoryTransactionsTable: React.FC<TableProps> = ({
  ref,
  className,
  categorySummary,
  transactions,
  loading,
  collapsing,
  exportOptions,
  sorting,
  onSortingChange,
  Footer,
  hideBalanceColumn,
  variant,
}) => {
  const enableSorting = !!onSortingChange && !!transactions?.length
  const hideBalances = hideBalanceColumn || (!!sorting && sorting.some((s) => s.id !== "timestamp"))
  const hasFooter = !!Footer

  const columns = useCategoryTransactionsTableColumns({
    variant,
    hasFooter,
    enableSorting,
  })

  const meta: CategoryTableMetadata = {
    variant,
    collapsing,
    exportOptions,
    categorySummary,
    Footer,
    loading,
  }

  const classNames = stringHelper.classNames(className, hideBalances && "hide-balances")
  return (
    <CategoryVTable
      ref={ref}
      className={classNames}
      loading={loading}
      meta={meta}
      collapsed={collapsing?.collapsed}
      transactions={transactions}
      columns={columns}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  )
}
